import React, { useState } from "react";
import ResortDetailModal from "../features/global-home/ui/ResortDetailModal";
import { getLanguageSection } from "../shared/services/languageService";

const GOLD = "#b8935a";

function IconBeach() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-7 w-7" aria-hidden="true">
      <path d="M12 20v-8.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M12 11.5c-2.4-2-6-1.6-7.6.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M12 11.5c2.4-2 6-1.6 7.6.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M12 11.5c-1-2.5.4-5.6 2.6-6.7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M12 11.5c1-2.5-.4-5.6-2.6-6.7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M4.5 20c2.2-1.2 12.8-1.2 15 0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function IconFamily() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-7 w-7" aria-hidden="true">
      <circle cx="12" cy="7" r="2.5" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="6.2" cy="9.2" r="2" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="17.8" cy="9.2" r="2" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8 19c0-2.6 1.7-4.3 4-4.3s4 1.7 4 4.3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M3 19c0-2.1 1.1-3.4 2.7-3.7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M21 19c0-2.1-1.1-3.4-2.7-3.7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function IconAward() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-7 w-7" aria-hidden="true">
      <circle cx="12" cy="9" r="5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8.8 13.2 7.3 21l4.7-2.6L16.7 21l-1.5-7.8" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="m12 6.4 1 2 2.2.3-1.6 1.5.4 2.2L12 11.6l-2 1.1.4-2.2-1.6-1.5 2.2-.3 1-2Z" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" />
    </svg>
  );
}

// Pareja tipográfica del hero: sans moderna para la marca, serif para la
// promesa. El contraste sans/serif es el recurso que usan las cadenas de lujo.
// Las fuentes se cargan en public/index.html.
const FONT_DISPLAY = "'Manrope', system-ui, -apple-system, 'Segoe UI', sans-serif";
const FONT_SERIF = "'Playfair Display', Georgia, 'Times New Roman', serif";

// Sombra apenas perceptible (blur amplio, opacidad baja): el texto "flota"
// sobre la foto en vez de llevar la típica sombra negra dura.
const TEXT_SHADOW = "0 2px 12px rgba(0,0,0,0.20)";
// Los textos chicos sí necesitan algo más de apoyo para sostener el contraste.
const TEXT_SHADOW_SOFT = "0 1px 10px rgba(0,0,0,0.35)";

// Relleno del título: degradado blanco → gris muy claro recortado al glifo, que
// da el acabado "iluminado". La sombra va como drop-shadow porque el texto en sí
// es transparente (lo pinta el fondo).
const TITLE_GRADIENT = {
  backgroundImage: "linear-gradient(to bottom, #ffffff 0%, #ffffff 45%, #e9ebf0 100%)",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  color: "transparent",
  filter: "drop-shadow(0 8px 12px rgba(0,0,0,0.20))"
};

const RULE_LEFT = `linear-gradient(to right, rgba(184,147,90,0), ${GOLD})`;
const RULE_RIGHT = `linear-gradient(to left, rgba(184,147,90,0), ${GOLD})`;

// Divisor dorado con palmera al centro. Se usa en el hero y en "Discover Our
// Resorts" (antes era el mismo marcado duplicado en los dos lugares).
function GoldDivider({ className = "" }) {
  return (
    <div className={`mx-auto flex max-w-xs items-center gap-3 ${className}`} aria-hidden="true">
      <span className="h-px flex-1" style={{ background: RULE_LEFT }} />
      <svg viewBox="0 0 24 24" className="h-5 w-5 flex-none" fill="none">
        <path d="M12.5 20c-.3-3.6.1-6.4 1.1-8.6" stroke={GOLD} strokeWidth="1.3" strokeLinecap="round" />
        <path d="M13.6 11.4c-2.2-1.9-5.3-1.9-7.1.3" stroke={GOLD} strokeWidth="1.3" strokeLinecap="round" />
        <path d="M13.6 11.4c2.3-1.8 5.4-1.4 6.9.9" stroke={GOLD} strokeWidth="1.3" strokeLinecap="round" />
        <path d="M13.6 11.4c-.6-2.7.7-5.3 3-6.2" stroke={GOLD} strokeWidth="1.3" strokeLinecap="round" />
        <path d="M13.6 11.4c1.3-2.5.6-5.3-1.5-6.6" stroke={GOLD} strokeWidth="1.3" strokeLinecap="round" />
      </svg>
      <span className="h-px flex-1" style={{ background: RULE_RIGHT }} />
    </div>
  );
}

function GlobalHome() {
  const language = getLanguageSection("globalhome");
  const heroImage = `${process.env.PUBLIC_URL}/img/guadalupe-parish.jpg`;
  // Resort cuyo detalle está abierto en el modal (null = modal cerrado).
  const [selectedResort, setSelectedResort] = useState(null);

  const base = process.env.PUBLIC_URL;
  // Cada resort trae dos versiones de la misma foto: el thumbnail que carga la
  // tarjeta y la grande que solo se descarga al abrir el modal.
  const resorts = [
    {
      name: language.resort1Name,
      image: `${base}/img/emotions-thumb.jpg`,
      largeImage: `${base}/img/emotions-large.jpg`,
      tagline: language.resort1Tagline,
      description: language.resort1Desc,
      perfectFor: language.resort1PerfectFor
    },
    {
      name: language.resort2Name,
      image: `${base}/img/distinct-thumb.jpg`,
      largeImage: `${base}/img/distinct-large.jpg`,
      tagline: language.resort2Tagline,
      description: language.resort2Desc,
      perfectFor: language.resort2PerfectFor
    },
    {
      name: language.resort3Name,
      image: `${base}/img/armony-thumb.jpg`,
      largeImage: `${base}/img/armony-large.jpg`,
      tagline: language.resort3Tagline,
      description: language.resort3Desc,
      perfectFor: language.resort3PerfectFor
    }
  ];

  const promises = [
    { icon: <IconFamily />, name: language.promise1Name, text: language.promise1Text },
    { icon: <IconAward />, name: language.promise2Name, text: language.promise2Text },
    { icon: <IconBeach />, name: language.promise3Name, text: language.promise3Text }
  ];

  return (
    <div id="globalhome" className="mr-ui bg-[#f3f5fb]">
      <ResortDetailModal
        resort={selectedResort}
        perfectForLabel={language.perfectForLabel}
        closeLabel={language.closeLabel}
        onClose={() => setSelectedResort(null)}
      />

      {/* Hero full-bleed. La foto va como <img> en el flujo (sin recorte: se ve
          completa) y el texto se superpone en la misma celda del grid, así el
          alto de la sección lo marca la proporción de la imagen — salvo en
          pantallas angostas, donde el texto es más alto y manda él. */}
      <section className="relative grid w-full overflow-hidden bg-marival-ink">
        <img
          src={heroImage}
          alt=""
          aria-hidden="true"
          className="block w-full object-cover"
          style={{ gridArea: "1 / 1" }}
        />

        {/* Velo oscuro ligero: lo justo para sostener el texto blanco sin apagar
            la foto (el textShadow de cada bloque hace el resto del trabajo). */}
        <div
          style={{
            gridArea: "1 / 1",
            background:
              "linear-gradient(to bottom, rgba(8,12,32,0.52) 0%, rgba(8,12,32,0.34) 45%, rgba(8,12,32,0.58) 100%)"
          }}
          aria-hidden="true"
        />

        <div
          className="mx-auto flex w-full max-w-6xl flex-col justify-start px-6 pb-14 pt-12 text-center md:px-8 md:pb-20 md:pt-16"
          style={{ gridArea: "1 / 1" }}
        >
          {/* Nivel 1 de jerarquía: dorado, chico y con mucho tracking. */}
          <div className="mx-auto flex max-w-xl items-center gap-5">
            <span className="h-px flex-1" style={{ background: RULE_LEFT }} aria-hidden="true" />
            <p
              className="m-0 whitespace-nowrap uppercase"
              style={{
                color: GOLD,
                fontFamily: FONT_DISPLAY,
                fontWeight: 700,
                letterSpacing: "0.35em",
                fontSize: "clamp(0.85rem, 1.2vw, 1.125rem)",
                textShadow: TEXT_SHADOW_SOFT
              }}
            >
              {language.heroTitleTop}
            </p>
            <span className="h-px flex-1" style={{ background: RULE_RIGHT }} aria-hidden="true" />
          </div>

          {/* Nivel 2: la marca. Sans pesada, tracking negativo, interlineado
              cerrado y relleno de degradado — se lee como logotipo, no como
              titular. */}
          <h1
            className="m-0 mt-8 uppercase"
            style={{
              fontFamily: FONT_DISPLAY,
              fontWeight: 800,
              lineHeight: 0.86,
              letterSpacing: "-0.02em",
              ...TITLE_GRADIENT
            }}
          >
            <span className="block" style={{ fontSize: "clamp(2.75rem, 10vw, 8.5rem)" }}>
              {language.heroTitleMain}
            </span>
            <span className="block" style={{ fontSize: "clamp(1.75rem, 6.5vw, 5.5rem)" }}>
              {language.heroTitleMainAccent}
            </span>
          </h1>

          {/* Nivel 3: la promesa, en serif para contrastar con la sans de
              arriba. Más chica que antes y en blanco roto, no blanco puro. */}
          <p
            className="m-0 mt-10 leading-snug"
            style={{
              color: "#F6F7FB",
              fontFamily: FONT_SERIF,
              fontWeight: 500,
              fontSize: "clamp(1.5rem, 2.8vw, 2.5rem)",
              textShadow: TEXT_SHADOW
            }}
          >
            {language.heroSuccessTitle}
          </p>

          <GoldDivider className="mt-9" />

          {/* Nivel 4: el párrafo, acotado a ~700px para que respire. */}
          <p
            className="m-0 mx-auto mt-9 text-white/90"
            style={{
              maxWidth: "44rem",
              fontFamily: FONT_DISPLAY,
              fontSize: "clamp(1.0625rem, 1.4vw, 1.25rem)",
              lineHeight: 1.75,
              textShadow: TEXT_SHADOW_SOFT
            }}
          >
            {language.heroSuccessText}
          </p>
        </div>
      </section>

      {/* Discover Our Resorts */}
      <section className="mx-auto w-full max-w-7xl px-5 py-16 md:px-8 md:py-20">
        <div className="text-center text-marival-navy">
          <h2 className="m-0 text-3xl font-bold leading-tight md:text-4xl">
            {language.resortsTitle}
          </h2>
          <GoldDivider className="mt-5" />
        </div>

        <div className="mt-12 grid gap-8 md:grid-cols-3">
          {resorts.map((resort, i) => (
            <article
              key={i}
              className="group relative flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-marival transition hover:-translate-y-1 hover:border-marival-blue hover:shadow-lg"
            >
              <div className="relative overflow-hidden">
                <img
                  src={resort.image}
                  alt={resort.name}
                  loading="lazy"
                  className="h-56 w-full object-cover transition duration-300 group-hover:scale-105"
                />
                <span
                  className="absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-full text-white opacity-0 transition group-hover:opacity-100"
                  style={{ background: "rgba(8,12,32,0.6)" }}
                  aria-hidden="true"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <path d="M4 10V4h6M20 14v6h-6M4 4l6.5 6.5M20 20l-6.5-6.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </div>

              <div className="flex flex-1 flex-col p-6">
                <h3 className="m-0 text-lg font-bold leading-6 text-marival-navy">{resort.name}</h3>
                <p className="m-0 mt-3 text-sm font-semibold uppercase tracking-[0.18em]" style={{ color: GOLD }}>
                  {resort.tagline}
                </p>
                <p className="m-0 mt-3 text-base leading-7 text-slate-600">{resort.description}</p>

                <div className="mt-5 border-t border-slate-100 pt-4">
                  <p className="m-0 text-xs font-semibold uppercase tracking-[0.15em] text-marival-navy">
                    {language.perfectForLabel}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {resort.perfectFor.map((tag, ti) => (
                      <span
                        key={ti}
                        className="rounded-full bg-[#eaf6fb] px-3 py-1 text-xs font-medium text-marival-sky"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Botón superpuesto: hace clicable toda la tarjeta sin meter
                  <h3>/<p> dentro de un <button> (HTML inválido). */}
              <button
                type="button"
                className="absolute inset-0 h-full w-full cursor-pointer border-0 bg-transparent p-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-marival-blue"
                onClick={() => setSelectedResort(resort)}
              >
                <span className="sr-only">{resort.name}</span>
              </button>
            </article>
          ))}
        </div>
      </section>

      {/* The Marival Promise */}
      <section className="bg-marival-navy text-white">
        <div className="mx-auto w-full max-w-7xl px-5 py-16 md:px-8 md:py-20">
          <div className="mx-auto max-w-3xl text-center">
            <p className="m-0 text-sm font-semibold uppercase tracking-[0.18em]" style={{ color: GOLD }}>
              {language.promiseEyebrow}
            </p>
            <h2 className="m-0 mt-4 text-3xl font-bold leading-tight md:text-4xl">
              {language.promiseTitle}
            </h2>
            <p className="m-0 mt-5 text-base leading-7 text-white/80">
              {language.promiseIntro}
            </p>
          </div>

          <div className="mt-14 grid gap-10 md:grid-cols-3">
            {promises.map((promise, i) => (
              <div className="flex flex-col items-center text-center" key={i}>
                <div
                  className="flex h-16 w-16 items-center justify-center rounded-full"
                  style={{ border: `2px solid ${GOLD}`, color: GOLD, background: "rgba(255,255,255,0.08)" }}
                >
                  {promise.icon}
                </div>
                <h3 className="m-0 mt-5 text-lg font-bold leading-6">{promise.name}</h3>
                <p className="m-0 mt-3 max-w-xs text-base leading-7 text-white/80">{promise.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default GlobalHome;
